import type { ComponentProps, ReactElement } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const FieldGroup = ({ className, ...props }: ComponentProps<'div'>): ReactElement => (
  <div
    data-slot="field-group"
    className={cn('group/field-group @container/field-group flex w-full flex-col gap-5', className)}
    {...props}
  />
);

const fieldVariants = cva('group/field flex w-full gap-2 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal:
        'flex-row items-center has-[>[data-slot=field-content]]:items-start *:data-[slot=field-label]:flex-auto',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

const Field = ({
  className,
  orientation = 'vertical',
  ...props
}: ComponentProps<'div'> & VariantProps<typeof fieldVariants>): ReactElement => (
  <div
    role="group"
    data-slot="field"
    data-orientation={orientation}
    className={cn(fieldVariants({ orientation }), className)}
    {...props}
  />
);

const FieldContent = ({ className, ...props }: ComponentProps<'div'>): ReactElement => (
  <div data-slot="field-content" className={cn('flex flex-1 flex-col gap-0.5 leading-snug', className)} {...props} />
);

const FieldLabel = ({ className, ...props }: ComponentProps<typeof Label>): ReactElement => (
  <Label
    data-slot="field-label"
    className={cn('flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50', className)}
    {...props}
  />
);

const FieldDescription = ({ className, ...props }: ComponentProps<'p'>): ReactElement => (
  <p
    data-slot="field-description"
    className={cn('text-left text-sm leading-normal font-normal text-muted-foreground', className)}
    {...props}
  />
);

const FieldError = ({ className, ...props }: ComponentProps<'div'>): ReactElement => (
  <div
    role="alert"
    data-slot="field-error"
    className={cn('text-sm font-normal text-destructive', className)}
    {...props}
  />
);

export { Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel };
